import { computed, inject, Injectable } from '@angular/core';
import { environment } from '../../../environment/environment';
import { UserStore } from '../stores/user.store';
import { AuthService } from './auth.service';
import { UserImpersonationService } from './user-impersonation.service';

/** Centralizes admin authorization logic */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly authService = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly impersonationService = inject(UserImpersonationService);

  // `bypassAuth` = MSAL token bypass (HTTP layer only). It does NOT grant admin rights.
  // `test-local` simulates a prod RBAC experience; only `dev` env bypasses permission checks.
  isDeveloperMode(): boolean {
    return environment.name === 'dev' && !this.impersonationService.isImpersonating();
  }

  readonly canAccessAdminFeatures = computed(() => {
    if (this.isDeveloperMode()) {
      return true;
    }

    const realUserId = this.authService.currentUserId();
    if (!realUserId) {
      return false;
    }

    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);
    if (!effectiveUserId) {
      return false;
    }

    const user = this.userStore.byId()(effectiveUserId);
    return user?.isAdmin ?? false;
  });

  readonly isAdminOrDev = this.canAccessAdminFeatures;
  readonly isDev = computed(() => this.isDeveloperMode());
}
